import { PROJECT_COLOR_COUNT } from '@jisy/shared';
import type { IssueParentRef, IssueTypeRef, PriorityRef, StatusRef, UserRef } from '@jisy/shared';
import { initialsOf } from '../lib/format.js';
import styles from './Badges.module.css';

/**
 * Плашки таблицы. Каждая читается не только цветом: у статуса есть точка и слово,
 * у типа — буква в рамке, у приоритета — код P1–P4. Это требование доступности из макета.
 */

/**
 * Jira различает всего три категории статусов, тогда как в макете нарисовано пять оттенков.
 * Изобретать недостающие два по названию статуса нельзя: названия у каждого проекта свои,
 * и «На ревью» с равным успехом может называться иначе. Поэтому цвет берётся из категории,
 * а различие внутри неё несёт само слово.
 */
function statusClass(category: StatusRef['category']): string {
  switch (category) {
    case 'new':
      return styles.statusNew ?? '';
    case 'indeterminate':
      return styles.statusProgress ?? '';
    case 'done':
      return styles.statusDone ?? '';
    default:
      return styles.statusNew ?? '';
  }
}

export function StatusBadge({ status }: { status: StatusRef | null }): React.ReactElement {
  if (!status) return <span className={styles.empty}>—</span>;

  return (
    <span className={`${styles.status} ${statusClass(status.category)}`}>
      <span className={styles.dot} aria-hidden="true" />
      <span className="truncate">{status.name}</span>
    </span>
  );
}

/** Буква типа: первая буква названия из Jira, потому что состав типов у каждого сайта свой. */
export function TypeBadge({ type }: { type: IssueTypeRef | null }): React.ReactElement {
  if (!type) return <span className={styles.empty}>—</span>;

  return (
    <span className={styles.type}>
      <span className={styles.typeGlyph} aria-hidden="true">
        {type.name.charAt(0).toUpperCase()}
      </span>
      <span className={`${styles.typeName} truncate`}>{type.name}</span>
    </span>
  );
}

/**
 * Родитель перед названием подзадачи: «WEB-14 / Форма карты», как в списках Jira. Нужен там,
 * где подзадача стоит сама по себе — в плане, в подборе, в таблице без своего родителя:
 * «Тесты» без задачи, к которой они относятся, мало что говорят.
 */
export function ParentPrefix({ parent }: { parent: IssueParentRef }): React.ReactElement {
  return (
    <span className={styles.parentPrefix}>
      <span className="mono">{parent.key}</span>
      <span aria-hidden="true"> / </span>
    </span>
  );
}

/**
 * Цвет чипа приоритета задаёт ранг, а не название: названия у каждого сайта Jira свои.
 * Ранг — это позиция в схеме приоритетов, и схема бывает длиннее четырёх: всё, что ниже
 * третьего места, красится самым спокойным оттенком, иначе цветов не напасёшься.
 */
function priorityClass(rank: number | null): string {
  switch (rank) {
    case 1:
      return styles.priority1 ?? '';
    case 2:
      return styles.priority2 ?? '';
    case 3:
      return styles.priority3 ?? '';
    default:
      return styles.priority4 ?? '';
  }
}

export function PriorityBadge({ priority }: { priority: PriorityRef | null }): React.ReactElement {
  if (!priority) return <span className={styles.empty}>—</span>;

  return (
    <span className={`${styles.priority} ${priorityClass(priority.rank)}`}>
      {priority.rank !== null && (
        <span className={`mono ${styles.priorityCode}`}>P{priority.rank}</span>
      )}
      <span className={`${styles.priorityName} truncate`}>{priority.name}</span>
    </span>
  );
}

export function Avatar({
  user,
  size = 22,
  strong = false,
}: {
  user: Pick<UserRef, 'displayName' | 'avatarUrl'> | null;
  size?: number;
  strong?: boolean;
}): React.ReactElement {
  const style = { width: size, height: size, fontSize: size <= 22 ? 10 : 11 };

  if (user?.avatarUrl) {
    return (
      <img
        className={styles.avatar}
        style={style}
        src={user.avatarUrl}
        alt=""
        loading="lazy"
        referrerPolicy="no-referrer"
      />
    );
  }

  return (
    <span
      className={`${styles.avatar} ${strong ? styles.avatarStrong : ''}`}
      style={style}
      aria-hidden="true"
    >
      {initialsOf(user?.displayName)}
    </span>
  );
}

/**
 * Цвет направления переменной токенов. Индексы идут по кругу: разделов бывает больше, чем
 * цветов в палитре. `null` — раздел ещё не загружен, и цвет выбирает вызывающий: у метки он
 * нейтральный, у отрезка плана — цвет действия.
 */
export function sectionColorVar(colorIndex: number | null): string | null {
  return colorIndex === null
    ? null
    : `var(--section-${(colorIndex % PROJECT_COLOR_COUNT) + 1})`;
}

/**
 * Цветная метка раздела слева от названия проекта. `null` — раздел ещё не загружен: место метка
 * занимает, но цвет берёт нейтральный, чтобы не мигнуть чужим и не сдвинуть строку.
 */
export function SectionMark({ colorIndex }: { colorIndex: number | null }): React.ReactElement {
  return (
    <span
      className={styles.sectionMark}
      style={{ background: sectionColorVar(colorIndex) ?? 'var(--color-line)' }}
      aria-hidden="true"
    />
  );
}
